/**
 * Welcome screen.
 *
 * Routing logic:
 *   - sessionState === 'new'           → "Start" → /setup (family onboarding)
 *   - sessionState === 'family-only'   → "Start" → /pick (child picker)
 *   - sessionState === 'active'        → "Start" → /map
 *
 * All three paths unlock the AudioContext on the first tap so subsequent
 * audio playback works on iOS Safari.
 */

import { useEffect } from 'preact/hooks';
import { useLocation } from 'preact-iso';
import { unlockAudio, audioUnlocked } from '../state/audio';
import { loadContent } from '../state/content';
import { sessionState } from '../state/user';

export function Welcome() {
  const { route } = useLocation();

  useEffect(() => {
    // Kick off content load early so the map feels instant.
    void loadContent();
  }, []);

  function handleStart() {
    unlockAudio();
    switch (sessionState.value) {
      case 'new':
        route('/setup');
        return;
      case 'family-only':
        route('/pick');
        return;
      case 'active':
        route('/map');
        return;
      default:
        // Still loading — retry shortly.
        setTimeout(handleStart, 250);
    }
  }

  const labelAr =
    sessionState.value === 'loading'
      ? '…'
      : audioUnlocked.value
        ? 'هَيَّا نَبْدَأُ'
        : 'اِضْغَطْ لِلْبَدْءِ';
  const labelEn =
    sessionState.value === 'loading'
      ? ''
      : audioUnlocked.value
        ? "Let's begin"
        : 'Tap to start';

  return (
    <div class="flex-1 flex flex-col items-center justify-center text-center px-6 py-10 gap-10">
      <div class="space-y-4">
        <h1 class="text-7xl md:text-9xl font-display text-coral drop-shadow-sm">مَرْحَبًا</h1>
        <p class="text-2xl md:text-3xl font-display text-ink/80">
          أَهْلًا بِكَ فِي عَالَمِ الحُرُوفِ
        </p>
        <p class="text-base md:text-lg text-ink/50" dir="ltr">
          Welcome to the world of letters
        </p>
      </div>

      <div class="text-6xl select-none" aria-hidden="true">
        🏝️ 🌊 ✨
      </div>

      <button
        type="button"
        onClick={handleStart}
        class="mt-4 min-h-[64px] px-10 py-4 rounded-2xl bg-coral text-white
               shadow-xl shadow-coral/40 flex flex-col items-center
               active:scale-95 hover:scale-[1.02] transition-transform"
      >
        <span class="text-2xl font-display">{labelAr}</span>
        {labelEn && (
          <span class="text-xs font-body opacity-85 mt-1" dir="ltr">
            {labelEn}
          </span>
        )}
      </button>

      <div class="mt-2 flex items-center gap-4 text-xs text-ink/40" dir="ltr">
        <span>huroof · v0.1 · built on Cloudflare</span>
        <span aria-hidden="true">·</span>
        <a href="/privacy" class="underline">
          Privacy
        </a>
      </div>
    </div>
  );
}
